<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import maplibregl from 'maplibre-gl';

const props = defineProps({
  points: {
    type: Array,
    default: () => []
  }
});

const mapContainer = ref(null);
const mapRef = ref(null);
const markersRef = ref([]);
const mapLoaded = ref(false);

const normalized = computed(() =>
  (props.points || [])
    .map((p) => {
      const lat = Number(p.latitude);
      const lng = Number(p.longitude);
      return Number.isFinite(lat) && Number.isFinite(lng)
        ? { ...p, latitude: lat, longitude: lng }
        : null;
    })
    .filter(Boolean)
);

const DEFAULT_CENTER = [37.618423, 55.751244];
const DEFAULT_ZOOM = 11;
const STYLE_URL = 'https://tiles.openfreemap.org/styles/positron';

function buildPopupContent(point) {
  const title = point.short_name || `Объект #${point.id}`;
  const address = point.address || '';
  const coords = `${point.latitude.toFixed(6)}, ${point.longitude.toFixed(6)}`;
  return `
    <div class="text-sm">
      <div class="font-medium mb-1">${title}</div>
      ${address ? `<div class="text-color-secondary">${address}</div>` : ''}
      <div class="mt-1 text-xs text-color-secondary">${coords}</div>
    </div>
  `;
}

function clearMarkers() {
  markersRef.value.forEach((m) => m.remove());
  markersRef.value = [];
}

function setMarkers(points) {
  if (!mapRef.value) return;
  clearMarkers();
  points.forEach((p) => {
    const popup = new maplibregl.Popup({ offset: 12 }).setHTML(buildPopupContent(p));
    const marker = new maplibregl.Marker({ color: '#0ea5e9' })
      .setLngLat([p.longitude, p.latitude])
      .setPopup(popup)
      .addTo(mapRef.value);
    markersRef.value.push(marker);
  });
}

function fitToMarkers(points) {
  const map = mapRef.value;
  if (!map || !points.length) return;
  const bounds = new maplibregl.LngLatBounds();
  points.forEach((p) => bounds.extend([p.longitude, p.latitude]));
  map.fitBounds(bounds, { padding: 20, maxZoom: 15 });
}

onMounted(() => {
  if (!mapContainer.value) return;
  const map = new maplibregl.Map({
    container: mapContainer.value,
    style: STYLE_URL,
    center: DEFAULT_CENTER,
    zoom: DEFAULT_ZOOM,
    attributionControl: true,
  });

  map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');

  map.on('load', () => {
    mapLoaded.value = true;
    setMarkers(normalized.value);
    fitToMarkers(normalized.value);
  });

  mapRef.value = map;
});

watch(
  () => normalized.value,
  (points) => {
    if (!mapLoaded.value) return;
    setMarkers(points);
    fitToMarkers(points);
  }
);

onBeforeUnmount(() => {
  clearMarkers();
  if (mapRef.value) {
    mapRef.value.remove();
    mapRef.value = null;
  }
});
</script>

<template>
  <div ref="mapContainer" class="h-full w-full rounded-xl overflow-hidden" />
</template>

<style scoped>
:global(.maplibregl-map) {
  font-family: inherit;
}
</style>
